import { isNumberEqual } from 'ag-charts-core';

import { BBox } from './bbox';

export const IDENTITY_MATRIX_ELEMENTS: readonly number[] = [1, 0, 0, 1, 0, 0];

/**
 * As of Jan 8, 2019, Firefox still doesn't implement
 * `getTransform(): DOMMatrix;`
 * `setTransform(transform?: DOMMatrix2DInit)`
 * in the `CanvasRenderingContext2D`.
 * Bug: https://bugzilla.mozilla.org/show_bug.cgi?id=928150
 * IE11 and Edge 44 also don't have the support.
 * Thus this class, to keep track of the current transform and
 * combine transformations.
 * Standards:
 * https://html.spec.whatwg.org/dev/canvas.html
 * https://www.w3.org/TR/geometry-1/
 */
export class Matrix {
    // Using column-major order.
    // When identifiers such as `m12` are used:
    // `1` means first column
    // `2` means second row

    private readonly elements: number[];

    get e() {
        return [...this.elements];
    }

    constructor(elements: readonly number[] = IDENTITY_MATRIX_ELEMENTS) {
        this.elements = [...elements];
    }

    setElements(elements: readonly number[]): this {
        const e = this.elements;

        // `this.elements = elements.slice()` is 4-5 times slower
        // (in Chrome 71 and FF 64) than manually copying elements,
        // since slicing allocates new memory.
        // The performance of passing parameters individually
        // vs as an array is about the same in both browsers, so we
        // go with a single (array of elements) parameter, because
        // `setElements(elements)` and `setElements([a, b, c, d, e, f])`
        // calls give us roughly the same performance, versus
        // `setElements(...elements)` and `setElements(a, b, c, d, e, f)`,
        // where the spread operator causes a 20-30x performance drop
        // (30x when compiled to ES5's `.apply(this, elements)`
        //  20x when used natively).
        e[0] = elements[0];
        e[1] = elements[1];
        e[2] = elements[2];
        e[3] = elements[3];
        e[4] = elements[4];
        e[5] = elements[5];

        return this;
    }

    get identity(): boolean {
        const e = this.elements;
        return (
            isNumberEqual(e[0], 1) &&
            isNumberEqual(e[1], 0) &&
            isNumberEqual(e[2], 0) &&
            isNumberEqual(e[3], 1) &&
            isNumberEqual(e[4], 0) &&
            isNumberEqual(e[5], 0)
        );
    }

    /**
     * Performs the AxB matrix multiplication and saves the result
     * to `C`, if given, or to `A` otherwise.
     */
    private AxB(A: number[], B: number[], C?: number[]) {
        const a = A[0] * B[0] + A[2] * B[1],
            b = A[1] * B[0] + A[3] * B[1],
            c = A[0] * B[2] + A[2] * B[3],
            d = A[1] * B[2] + A[3] * B[3],
            e = A[0] * B[4] + A[2] * B[5] + A[4],
            f = A[1] * B[4] + A[3] * B[5] + A[5];

        C = C ?? A;
        C[0] = a;
        C[1] = b;
        C[2] = c;
        C[3] = d;
        C[4] = e;
        C[5] = f;
    }

    /**
     * The `other` matrix gets post-multiplied to the current matrix.
     * Returns the current matrix.
     * @param other
     */
    multiplySelf(other: Matrix): this {
        this.AxB(this.elements, other.elements);

        return this;
    }

    /**
     * The `other` matrix gets post-multiplied to the current matrix.
     * Returns a new matrix.
     * @param other
     */
    multiply(other: Matrix | DOMMatrix): Matrix {
        const elements = [Number.NaN, Number.NaN, Number.NaN, Number.NaN, Number.NaN, Number.NaN];

        if (other instanceof Matrix) {
            this.AxB(this.elements, other.elements, elements);
        } else {
            this.AxB(this.elements, [other.a, other.b, other.c, other.d, other.e, other.f], elements);
        }

        return new Matrix(elements);
    }

    preMultiplySelf(other: Matrix): this {
        this.AxB(other.elements, this.elements, this.elements);

        return this;
    }

    /**
     * Returns the inverse of this matrix as a new matrix.
     */
    inverse(): Matrix {
        const el = this.elements;
        let a = el[0],
            b = el[1],
            c = el[2],
            d = el[3];
        const e = el[4],
            f = el[5];
        const rD = 1 / (a * d - b * c); // reciprocal of determinant

        a *= rD;
        b *= rD;
        c *= rD;
        d *= rD;

        return new Matrix([d, -b, -c, a, c * f - d * e, b * e - a * f]);
    }

    invertSelf(): this {
        const el = this.elements;
        let a = el[0],
            b = el[1],
            c = el[2],
            d = el[3];
        const e = el[4],
            f = el[5];
        const rD = 1 / (a * d - b * c); // reciprocal of determinant

        a *= rD;
        b *= rD;
        c *= rD;
        d *= rD;

        el[0] = d;
        el[1] = -b;
        el[2] = -c;
        el[3] = a;
        el[4] = c * f - d * e;
        el[5] = b * e - a * f;

        return this;
    }

    transformPoint(x: number, y: number): { x: number; y: number } {
        const e = this.elements;
        return {
            x: x * e[0] + y * e[2] + e[4],
            y: x * e[1] + y * e[3] + e[5],
        };
    }

    transformBBox(bbox: BBox, target?: BBox): BBox {
        const el = this.elements;
        const xx = el[0];
        const xy = el[1];
        const yx = el[2];
        const yy = el[3];

        const h_w = bbox.width * 0.5;
        const h_h = bbox.height * 0.5;
        const cx = bbox.x + h_w;
        const cy = bbox.y + h_h;

        const w = Math.abs(h_w * xx) + Math.abs(h_h * yx);
        const h = Math.abs(h_w * xy) + Math.abs(h_h * yy);

        target ??= new BBox(0, 0, 0, 0);

        target.x = cx * xx + cy * yx + el[4] - w;
        target.y = cx * xy + cy * yy + el[5] - h;
        target.width = w + w;
        target.height = h + h;

        return target;
    }

    toContext(ctx: CanvasTransform) {
        // It's fair to say that matrix multiplications are not cheap.
        // However, updating path definitions on every frame isn't either, so
        // it may be cheaper to just translate paths. It's also fair to
        // say, that most paths will have to be re-rendered anyway, say
        // rectangle paths in a bar chart, where an animation would happen when
        // the data set changes and existing bars are morphed into new ones.
        // Or a pie chart, where old sectors are also morphed into new ones.
        // Same for the line chart. The only plausible case where translating
        // existing paths would be enough, is the scatter chart, where marker
        // icons, typically circles, stay the same size. But if circle radii
        // are bound to some data points, even circle paths would have to be
        // updated. And thus it makes sense to optimize for fewer matrix
        // transforms, where transform matrices of paths are mostly identity
        // matrices and `x`/`y`, `centerX`/`centerY` and similar properties
        // are used to define a path at specific coordinates. And only groups
        // are used to collectively apply a transform to a set of nodes.

        // If the matrix is mostly identity (95% of the time),
        // the `if (this.isIdentity)` check can make this call 3-4 times
        // faster on average: https://jsperf.com/matrix-check-first-vs-always-set
        if (this.identity) {
            return;
        }

        const e = this.elements;
        ctx.transform(e[0], e[1], e[2], e[3], e[4], e[5]);
    }

    static updateTransformMatrix(
        matrix: Matrix,
        scalingX: number,
        scalingY: number,
        rotation: number,
        translationX: number,
        translationY: number,
        opts?: {
            scalingCenterX?: number;
            scalingCenterY?: number;
            rotationCenterX?: number;
            rotationCenterY?: number;
        }
    ) {
        const sx = scalingX;
        const sy = scalingY;
        let scx: number;
        let scy: number;

        if (sx === 1 && sy === 1) {
            scx = 0;
            scy = 0;
        } else {
            scx = opts?.scalingCenterX ?? 0;
            scy = opts?.scalingCenterY ?? 0;
        }

        const r = rotation;
        const cos = Math.cos(r);
        const sin = Math.sin(r);
        let rcx: number;
        let rcy: number;

        if (r === 0) {
            rcx = 0;
            rcy = 0;
        } else {
            rcx = opts?.rotationCenterX ?? 0;
            rcy = opts?.rotationCenterY ?? 0;
        }

        const tx = translationX;
        const ty = translationY;

        // The transform matrix `M` is a result of the following transformations:
        // 1) translate the center of scaling to the origin
        // 2) scale
        // 3) translate back
        // 4) translate the center of rotation to the origin
        // 5) rotate
        // 6) translate back
        // 7) translate
        //         (7)          (6)             (5)             (4)           (3)           (2)           (1)
        //     | 1 0 tx |   | 1 0 rcx |   | cos -sin 0 |   | 1 0 -rcx |   | 1 0 scx |   | sx 0 0 |   | 1 0 -scx |
        // M = | 0 1 ty | * | 0 1 rcy | * | sin  cos 0 | * | 0 1 -rcy | * | 0 1 scy | * | 0 sy 0 | * | 0 1 -scy |
        //     | 0 0  1 |   | 0 0  1  |   |  0    0  1 |   | 0 0  1   |   | 0 0  1  |   | 0  0 0 |   | 0 0  1   |

        // Translation after steps 1-4 above:
        const tx4 = scx * (1 - sx) - rcx;
        const ty4 = scy * (1 - sy) - rcy;

        matrix.setElements([
            cos * sx,
            sin * sx,
            -sin * sy,
            cos * sy,
            cos * tx4 - sin * ty4 + rcx + tx,
            sin * tx4 + cos * ty4 + rcy + ty,
        ]);

        return matrix;
    }
}
